import type { App, DirectiveBinding } from 'vue';

const observerCache = new Map<string, IntersectionObserver>();
const observedElements = new WeakSet<Element>();
const elementObserverMap = new WeakMap<Element, IntersectionObserver>();

// 检测是否为移动端
function isMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  ) || window.innerWidth <= 768;
}

// 检查元素是否已经在视口内
function isElementInViewport(el: HTMLElement): boolean {
  if (typeof window === 'undefined') return false;

  const rect = el.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= windowHeight &&
    rect.right <= windowWidth
  );
}

// 检查元素是否部分在视口内
function isElementPartiallyInViewport(el: HTMLElement, threshold: number = 0.2): boolean {
  if (typeof window === 'undefined') return false;

  const rect = el.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
  const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0);
  const visibleArea = visibleHeight * visibleWidth;
  const elementArea = rect.width * rect.height;

  return elementArea > 0 && visibleArea / elementArea >= threshold;
}

function getObserver(threshold: number, rootMargin: string) {
  if (typeof IntersectionObserver === 'undefined') {
    return null;
  }

  const observerKey = `${threshold}|${rootMargin}`;
  if (!observerCache.has(observerKey)) {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scroll-animate-in');
            entry.target.classList.remove('scroll-animate-out');
            obs.unobserve(entry.target);
            observedElements.delete(entry.target);
            elementObserverMap.delete(entry.target);
          }
        });
      },
      {
        // 默认当元素进入视口 20% 时触发
        threshold,
        rootMargin,
        // 移动端使用 root: null 以确保使用视口作为根
        root: null,
      },
    );
    observerCache.set(observerKey, observer);
  }

  return observerCache.get(observerKey)!;
}

const scrollAnimate = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('scroll-animate-in');
      return;
    }

    const options = binding.value || {};
    const delay = options.delay || 0;
    const offset = options.offset || 30;
    const threshold = options.threshold ?? 0.2;
    // 移动端使用更宽松的 rootMargin，确保元素更容易被检测到
    const defaultRootMargin = isMobile()
      ? '0px 0px -20px 0px'
      : '0px 0px -50px 0px';
    const rootMargin = options.rootMargin ?? defaultRootMargin;

    if (offset !== 30) {
      el.style.setProperty('--scroll-animate-offset', `${offset}px`);
    }

    el.classList.add('scroll-animate-out');

    const observeElement = () => {
      // 使用 requestAnimationFrame 确保在正确的时机检查
      requestAnimationFrame(() => {
        // 如果元素已经在视口内，直接添加动画类
        if (isElementPartiallyInViewport(el, threshold)) {
          el.classList.add('scroll-animate-in');
          el.classList.remove('scroll-animate-out');
          return;
        }

        const observer = getObserver(threshold, rootMargin);
        if (observer && !observedElements.has(el)) {
          observedElements.add(el);
          observer.observe(el);
          elementObserverMap.set(el, observer);
        }
      });
    };

    if (delay > 0) {
      setTimeout(observeElement, delay);
    } else {
      observeElement();
    }
  },

  unmounted(el: HTMLElement) {
    if (observedElements.has(el)) {
      const observer = elementObserverMap.get(el);
      observer?.unobserve(el);
      observedElements.delete(el);
      elementObserverMap.delete(el);
    }
  },
};

export function setupScrollAnimate(app: App) {
  app.directive('scroll-animate', scrollAnimate);
}

export { scrollAnimate };
